import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Scale, AlertCircle } from 'lucide-react';
const Terms = () => {

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 right-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-red-600/30 rounded-full blur-[80px] md:blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[250px] md:w-[500px] h-[250px] md:h-[500px] bg-blue-600/20 rounded-full blur-[60px] md:blur-[100px] translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mt-5 inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-red-600/20 mb-4 md:mb-6">
              <Scale size={24} className="text-red-500 md:w-8 md:h-8" />
            </div>
            <h1 className="text-3xl text-gray-100 md:text-5xl font-serif font-bold mb-3 md:mb-4">
              Terms of Service
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">
              Please read these terms carefully before using our services.
            </p>
            <p className="text-xs md:text-sm text-slate-400 mt-4">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-10 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
            className="space-y-6 md:space-y-8"
          >
            {/* Introduction */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900 mb-3 md:mb-4">Agreement to Terms</h2>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                By accessing or using the services of 6th Sense Training Academy ("we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </motion.div>

            {/* Services */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="text-red-600 w-5 h-5 md:w-6 md:h-6" />
                <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900">Our Services</h2>
              </div>
              <div className="text-sm md:text-base text-slate-600 space-y-3">
                <p>6th Sense Training Academy provides:</p>
                <ul className="list-disc list-inside space-y-2 ml-2 md:ml-4 marker:text-red-500">
                  <li>IELTS, OET, PTE, and DET training courses</li>
                  <li>Study abroad consultancy and admission guidance</li>
                  <li>Visa application assistance</li>
                  <li>University partnership services</li>
                  <li>Mock tests and practice sessions</li>
                  <li>Free counselling and demo classes</li>
                </ul>
                <p className="mt-4 pt-2 border-t border-slate-100 text-xs md:text-sm text-slate-500 italic">
                  We reserve the right to modify, suspend, or discontinue any service at any time without prior notice.
                </p>
              </div>
            </motion.div>

            {/* User Conduct */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="text-red-600 w-5 h-5 md:w-6 md:h-6" />
                <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900">User Conduct</h2>
              </div>
              <div className="text-sm md:text-base text-slate-600">
                <p className="mb-3">You agree not to:</p>
                <ul className="list-disc list-inside space-y-2 ml-2 md:ml-4 marker:text-red-500">
                  <li>Use our services for any illegal or unauthorized purpose</li>
                  <li>Violate any laws in your jurisdiction</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Transmit any viruses, malware, or harmful code</li>
                  <li>Interfere with or disrupt our services or servers</li>
                </ul>
              </div>
            </motion.div>

            {/* Payment & Refunds */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900 mb-4">Payment & Refunds</h2>
              <div className="space-y-6 text-sm md:text-base text-slate-600">
                <div>
                  <h3 className="font-bold text-slate-800 mb-2">Course Fees</h3>
                  <ul className="list-disc list-inside space-y-1 ml-2 md:ml-4">
                    <li>Fees are specified at the time of registration.</li>
                    <li>Payments must be made in full or via agreed installment plans.</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-2">Refund Policy</h3>
                  <ul className="list-disc list-inside space-y-1 ml-2 md:ml-4">
                    <li>Refunds are subject to specific course policies.</li>
                    <li>No refunds for completed courses.</li>
                    <li>Written requests are required for any refund claims.</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Governing Law */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900 mb-3">Governing Law</h2>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                These Terms of Service shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts in Trichy, Tamil Nadu, India.
              </p>
            </motion.div>

            {/* Contact Information */}
            <motion.div variants={fadeInUp} className="bg-gradient-to-br from-red-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border border-red-100">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900 mb-4">Contact Us</h2>
              <p className="text-sm md:text-base text-slate-700 mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="space-y-2 text-sm md:text-base text-slate-700">
                <p><span className="font-bold">Email:</span> manager.6thsense@gmail.com</p>
                <p><span className="font-bold">Phone:</span> +91 63837 03636</p>
                <p><span className="font-bold">Address:</span> 2nd Floor, Vignesh Plaza, 202, Thillai Nagar Main Rd, above Domino's Pizza, Tennur, Tiruchirappalli, Tamil Nadu 620018, India</p>
              </div>
            </motion.div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms;